import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useAuthenticator } from '@aws-amplify/ui-react';
import CloseIcon from '@mui/icons-material/Close';
import { Box, Button, CircularProgress, SelectChangeEvent, Typography } from '@mui/material';
import * as Sentry from '@sentry/react';

import CustomBackdrop from '../../../../components/CustomBackdrop/CustomBackdrop';
import {
  useCreatePdOrganizationMutation,
  useGetBuyAndBuildPlatformsQuery,
  usePrecalculateAllKpisMutation,
  useCortexFeedbackPipedriveBridgeMutation,
} from '../../../../gql/generated/graphql';
import { paths } from '../../../../routes/paths';
import { ValidationSteps } from '../../types';
import BIDPlatform from '../BIDPlatform/BIDPlatform';

// Map validation steps to their implicit irrelevant reasons
const stepToReasonMap: Record<ValidationSteps, string | null> = {
  [ValidationSteps.IS_SOFTWARE]: null, // No rejection on this step
  [ValidationSteps.VALIDATE_SIZE]: 'tooSmall',
  [ValidationSteps.VALIDATE_ADDRESS]: 'wrongGeography',
  [ValidationSteps.VALIDATE_SHAREHOLDING_MANAGEMENT]: 'otherOwnership',
  [ValidationSteps.VALIDATE_PRODUCT_CATEGORY]: 'wrongBusinessModel',
  [ValidationSteps.VALIDATE_KEYWORDS]: 'wrongBusinessModel',
  [ValidationSteps.VALIDATE_CUSTOMERS]: 'b2c',
  [ValidationSteps.VALIDATE_TECHNOLOGY]: 'wrongBusinessModel',
  [ValidationSteps.FINISH]: null, // Already has its own mechanism
};

type Props = {
  companyId: number;
  activeStep: ValidationSteps;
};

const NotRelevantRejection = ({ companyId, activeStep }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuthenticator();

  const [isExpanded, setIsExpanded] = useState(false);
  const [platform, setPlatform] = useState<string | null>(null);

  // Lazy load platforms only when expanded
  const { data: platformsData, loading: platformsLoading } = useGetBuyAndBuildPlatformsQuery({
    variables: { sortForTargetId: companyId, excludeAddOns: true },
    skip: !isExpanded,
    fetchPolicy: 'cache-first',
    nextFetchPolicy: 'cache-first',
  });

  const buyAndBuildPlatformsNodes = platformsData?.getBuyAndBuildPlatforms?.edges?.map(edge => edge?.node);

  const [createPDOrganization, { loading: createPDOrganizationLoading }] = useCreatePdOrganizationMutation();
  const [precalculateAllKpis, { loading: precalculateAllKpisLoading }] = usePrecalculateAllKpisMutation();
  const [addBidPlatform, { loading: addBidPlatformLoading }] = useCortexFeedbackPipedriveBridgeMutation();

  const reason = stepToReasonMap[activeStep];

  // Don't render if there's no reason for this step
  if (!reason) {
    return null;
  }

  const handleToggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  const handleChangePlatform = (event: SelectChangeEvent) => {
    setPlatform(event.target.value);
  };

  const handleSubmit = async () => {
    try {
      const authId = user.username.replace('GoogleWorkspace_', '');

      // Build form data with the implicit reason set to true
      const formData = {
        newPlatform: false,
        addOnExisting: false,
        addOnFuture: false,
        saas: false,
        venture: false,
        none: false,
        partOfGroup: false,
        otherOwnership: reason === 'otherOwnership',
        sellsHardware: false,
        tooMuchConsulting: false,
        reseller: false,
        wrongBusinessModel: reason === 'wrongBusinessModel',
        b2c: reason === 'b2c',
        wrongGeography: reason === 'wrongGeography',
        tooBig: false,
        tooSmall: reason === 'tooSmall',
        createOrganization: false,
        createDeal: false,
        selectValue: '',
        note: '',
      };

      const variables = {
        companyId,
        authId,
        ...formData,
      };

      createPDOrganization({ variables });
      precalculateAllKpis({ variables: { companyId } });

      if (platform) {
        addBidPlatform({ variables: { addOnForBidId: parseInt(platform), companyId } });
      }

      navigate(paths.crawlerInbox);
    } catch (error) {
      console.error('Error submitting rejection:', error);
      Sentry.captureException(error);
    }
  };

  const isLoading = createPDOrganizationLoading || precalculateAllKpisLoading || addBidPlatformLoading;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        mt: 4,
        pt: 2,
      }}
    >
      <Box
        onClick={handleToggleExpand}
        sx={{
          display: 'flex',
          alignItems: 'center',
          cursor: 'pointer',
          color: 'text.secondary',
          '&:hover': {
            color: 'error.main',
          },
        }}
      >
        <CloseIcon sx={{ fontSize: 18, mr: 0.5 }} />
        <Typography variant="body2">{t('validation.notRelevant.label')}</Typography>
      </Box>

      {isExpanded && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            mt: 1.5,
            width: '100%',
            maxWidth: 400,
          }}
        >
          <Box sx={{ flex: 1 }}>
            {platformsLoading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={20} />
                <Typography variant="body2" color="text.secondary">
                  {t('validation.notRelevant.loading')}
                </Typography>
              </Box>
            ) : (
              <BIDPlatform
                platform={platform}
                platforms={buyAndBuildPlatformsNodes}
                onHandleChangePlatform={handleChangePlatform}
              />
            )}
          </Box>
          <Button variant="text" color="error" onClick={handleSubmit} disabled={isLoading} sx={{ minWidth: 100 }}>
            {t('validation.notRelevant.submit')}
          </Button>
        </Box>
      )}

      <CustomBackdrop open={isLoading} />
    </Box>
  );
};

export default NotRelevantRejection;
